import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

// Utils
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import { HEADER_LOTTIE_ASSETS } from "@swiggy-private/core-ui";

// context
import { CartContext } from "@cart-client/contexts/CartContext";

// helper
import { processCartData } from "@cart-client/helpers/cartHeaderLottie";

export const usePrefetchCartHeaderLottie = () => {
    const expressLottieFetchRef = useRef<boolean>(false);
    const nonexpressLottieFetchRef = useRef<boolean>(false);
    const otgLottieFetchRef = useRef<boolean>(false);
    const boltLottieFetchRef = useRef<boolean>(false);
    const boltStampLottieFetchRef = useRef<boolean>(false);
    const fetchedCartTypeRef = useRef<Set<string>>(new Set());

    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const lottieList = useMemo(
        () => cartResponse?.data?.cart_header_widget_presentation?.header_lottie_config?.lotties || [],
        [cartResponse],
    );
    const cartType = useMemo(() => cartResponse?.data?.cart_header_widget_presentation?.cart_type || "", [
        cartResponse,
    ]);

    const { isOtg, isExpress, frequencyData, isBolt, isBlck } = useMemo(() => processCartData(cartResponse), [
        cartResponse,
    ]);

    const fetchExpressLottie = useCallback(() => {
        if (isExpress && !expressLottieFetchRef.current) {
            fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.EXPRESS_TRANSITION) || "")
                .then(() => {
                    expressLottieFetchRef.current = true;
                })
                .catch();
        }
    }, [isExpress]);

    const fetchNonExpressLottie = useCallback(() => {
        if (!isExpress && isOtg && !nonexpressLottieFetchRef.current) {
            fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.NON_EXPRESS_TRANSITION) || "")
                .then(() => {
                    nonexpressLottieFetchRef.current = true;
                })
                .catch();
        }
    }, [isExpress, isOtg]);

    const fetchOtgLottie = useCallback(() => {
        if (isOtg && !otgLottieFetchRef.current) {
            fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.OTG_STAMP) || "")
                .then(() => {
                    otgLottieFetchRef.current = true;
                })
                .catch();
        }
    }, [isOtg]);

    const fetchBoltLottie = useCallback(() => {
        if (!boltLottieFetchRef.current) {
            const transitionLottiesToFetch = [
                fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.BOLT_TRANSITION) || ""),
                fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.BOLT_RANGE_SLA_TRANSITION) || ""),
            ];
            Promise.all(transitionLottiesToFetch)
                .then(() => {
                    boltLottieFetchRef.current = true;
                })
                .catch();
        }
    }, []);

    const fetchBoltStampLottie = useCallback(() => {
        if (!boltStampLottieFetchRef.current) {
            const stampsToFetch = [];

            if (isBlck && isOtg) {
                stampsToFetch.push(fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.BOLT_OTG_STAMP) || ""));
            } else {
                stampsToFetch.push(
                    fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.BOLT_SLA_10_STAMP) || ""),
                    fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.BOLT_SLA_15_STAMP) || ""),
                    fetch(getCloudinaryUrl(HEADER_LOTTIE_ASSETS.BOLT_RANGE_SLA_STAMP) || ""),
                );
            }

            Promise.allSettled(stampsToFetch).then(() => {
                boltStampLottieFetchRef.current = true;
            });
        }
    }, [isBlck, isOtg]);

    // -------------------- START OF COMMON LOGIC --------------------

    useEffect(() => {
        if (!cartType || fetchedCartTypeRef.current.has(cartType)) {
            return;
        }
        const lottieToFetch = lottieList.map(lottieItem => fetch(getCloudinaryUrl(lottieItem?.lottie_id) || ""));
        Promise.allSettled(lottieToFetch).then(() => {
            fetchedCartTypeRef.current.add(cartType);
        });
    }, [lottieList, cartType]);

    // -------------------- END OF COMMON LOGIC --------------------

    useEffect(() => {
        if (isBolt) {
            fetchBoltLottie();
            fetchBoltStampLottie();
        } else if (isEmpty(frequencyData)) {
            return;
        } else {
            fetchExpressLottie();
            fetchNonExpressLottie();
            fetchOtgLottie();
        }
    }, [
        fetchBoltStampLottie,
        fetchExpressLottie,
        fetchNonExpressLottie,
        fetchOtgLottie,
        fetchBoltLottie,
        frequencyData,
        isBolt,
        isBlck,
        isExpress,
        isOtg,
    ]);
};
